"use client";

import { CmsContainedImage } from "@/components/cms/CmsContainedImage";
import Link from "next/link";
import { safeHref } from "@/lib/safe-href";
import { cn } from "@/lib/utils";

export type CardItem = {
  icon?: string;
  image_url?: string;
  title?: string;
  body?: string;
  cta_label?: string;
  cta_url?: string;
};

export type CardsSectionData = {
  eyebrow?: string;
  heading?: string;
  subheading?: string;
  columns?: number | string;
  items?: CardItem[];
};

const colsClass: Record<number, string> = {
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-2 lg:grid-cols-3",
  4: "sm:grid-cols-2 lg:grid-cols-4",
};

export function CardsSection({ data }: { data: CardsSectionData }) {
  const items = Array.isArray(data.items) ? data.items : [];
  const colsRaw = typeof data.columns === "string" ? parseInt(data.columns, 10) : data.columns;
  const cols = colsRaw === 2 || colsRaw === 3 || colsRaw === 4 ? colsRaw : 3;
  const gridCols = colsClass[cols];

  if (items.length === 0 && !data.heading) return null;

  return (
    <section className="cms-section-contained bg-gradient-to-b from-[var(--background)] via-[var(--section-alt)]/50 to-[var(--background)] py-[var(--section-space)]">
      <div className="mx-auto w-full max-w-6xl min-w-0 px-4 md:px-6">
        {(data.eyebrow || data.heading || data.subheading) && (
          <header className="mx-auto mb-12 max-w-3xl text-center md:mb-16">
            {data.eyebrow ? (
              <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-[var(--accent)]">
                {data.eyebrow}
              </p>
            ) : null}
            {data.heading ? (
              <h2 className="text-balance text-[clamp(1.75rem,3.2vw,2.5rem)] font-extrabold tracking-tight text-[var(--primary)]">
                {data.heading}
              </h2>
            ) : null}
            {data.subheading ? (
              <p className="mx-auto mt-4 max-w-2xl text-pretty text-base leading-relaxed text-[var(--muted-foreground)] md:text-lg">
                {data.subheading}
              </p>
            ) : null}
          </header>
        )}

        {items.length > 0 && (
          <div className={cn("cms-section-grid grid min-w-0 gap-6", gridCols)}>
            {items.map((item, idx) => {
              const hasImage = Boolean(item.image_url?.trim());
              const hasCta = !!(item.cta_label && item.cta_url);
              const card = (
                <article
                  className={cn(
                    "group relative flex h-full min-w-0 max-w-full flex-col overflow-hidden rounded-2xl border border-[var(--border)]/80 bg-[var(--card)] p-7 shadow-sm transition-all duration-200",
                    "motion-safe:hover:-translate-y-1 motion-safe:hover:shadow-xl motion-safe:hover:border-[var(--accent)]/40",
                    "before:absolute before:inset-x-0 before:top-0 before:h-1 before:bg-gradient-to-r before:from-[var(--accent)] before:to-[var(--primary)] before:opacity-0 motion-safe:before:transition-opacity motion-safe:group-hover:before:opacity-100"
                  )}
                >
                  {hasImage ? (
                    <CmsContainedImage
                      src={item.image_url}
                      alt={item.title ?? ""}
                      framed
                      frameClassName="cms-card-image mb-5 aspect-[16/10] w-full max-w-full bg-[var(--muted)]"
                    />
                  ) : item.icon ? (
                    <div
                      className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--primary)]/5 text-2xl text-[var(--primary)]"
                      aria-hidden
                    >
                      {item.icon}
                    </div>
                  ) : null}

                  {item.title ? (
                    <h3 className="text-lg font-bold leading-snug tracking-tight text-[var(--primary)] md:text-xl">
                      {item.title}
                    </h3>
                  ) : null}
                  {item.body ? (
                    <p className="mt-3 flex-1 text-pretty text-sm leading-relaxed text-[var(--muted-foreground)] md:text-base">
                      {item.body}
                    </p>
                  ) : null}
                  {hasCta ? (
                    <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[var(--primary)] motion-safe:transition-colors group-hover:text-[var(--accent-dark)]">
                      {item.cta_label}
                      <span aria-hidden className="motion-safe:transition-transform motion-safe:group-hover:translate-x-0.5">→</span>
                    </span>
                  ) : null}
                </article>
              );

              if (hasCta) {
                return (
                  <Link
                    key={idx}
                    href={safeHref(item.cta_url!)}
                    className="block h-full rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2"
                  >
                    {card}
                  </Link>
                );
              }
              return <div key={idx} className="h-full">{card}</div>;
            })}
          </div>
        )}
      </div>
    </section>
  );
}
